import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { LocalAudioTrack, RemoteAudioTrack, Room } from 'twilio-video';
import useRoomState from '../../hooks/useRoomState/useRoomState';
import useVideoContext from '../../hooks/useVideoContext/useVideoContext';
import { detectSessionUserType, detectUserTypeFromIdentity } from '../../utils/videoJoinToken';
import { mergeAudioBlobs } from '../../utils/audioToWav';
import { generateNotaReportFromAudio } from '../../utils/notaReportApi';
import { consumeSessionEndConfirmed } from '../../utils/sessionEnd';

const useStyles = makeStyles(() => ({
  disclaimer: {
    backgroundColor: '#fff3cd',
    border: '1px solid #ffc107',
    padding: 16,
    borderRadius: 4,
    marginTop: 24,
    maxWidth: 500,
  },
  errorBox: {
    backgroundColor: '#f8d7da',
    border: '1px solid #f5c6cb',
    padding: 16,
    borderRadius: 4,
    color: '#721c24',
  },
}));

function collectAudioTracks(room: Room): MediaStreamTrack[] {
  const tracks: MediaStreamTrack[] = [];
  Array.from(room.localParticipant.audioTracks.values()).forEach(publication => {
    const track = publication.track as LocalAudioTrack | null;
    if (track && track.mediaStreamTrack) {
      tracks.push(track.mediaStreamTrack);
    }
  });
  room.participants.forEach(participant => {
    Array.from(participant.audioTracks.values()).forEach(publication => {
      const track = publication.track as RemoteAudioTrack | null;
      if (track && track.mediaStreamTrack) {
        tracks.push(track.mediaStreamTrack);
      }
    });
  });
  return tracks;
}

function pickRecorderMimeType(): string {
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];
  for (let i = 0; i < candidates.length; i++) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(candidates[i])) {
      return candidates[i];
    }
  }
  return '';
}

/**
 * Doctor-only: mix local + remote audio during the visit, then call NotaReport
 * only after the doctor confirms the real end of the session.
 */
export default function NotaReportUploader() {
  const classes = useStyles();
  const { room } = useVideoContext();
  const roomState = useRoomState();
  const identity = room?.localParticipant?.identity || '';
  const isDoctor = detectSessionUserType(identity) === 'doctor' || detectUserTypeFromIdentity(identity) === 'doctor';
  const disabled = process.env.REACT_APP_DISABLE_NOTA_REPORT === 'true';

  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const destinationRef = useRef<MediaStreamAudioDestinationNode | null>(null);
  const sourcesRef = useRef<Map<string, MediaStreamAudioSourceNode>>(new Map());
  const chunksRef = useRef<Blob[]>([]);
  const sessionBlobsRef = useRef<Blob[]>([]);
  const stopResolveRef = useRef<((blob: Blob | null) => void) | null>(null);
  const attemptedRef = useRef(false);
  const connectedOnceRef = useRef(false);
  const roomNameRef = useRef<string>('');
  const startRetryRef = useRef<any>(null);

  const connectTrack = useCallback((track: MediaStreamTrack) => {
    const audioContext = audioContextRef.current;
    const destination = destinationRef.current;
    if (!audioContext || !destination || sourcesRef.current.has(track.id)) {
      return;
    }
    try {
      const source = audioContext.createMediaStreamSource(new MediaStream([track]));
      source.connect(destination);
      sourcesRef.current.set(track.id, source);
    } catch (err) {
      console.warn('Could not mix audio track for medical report', err);
    }
  }, []);

  const startRecording = useCallback(
    (activeRoom: Room) => {
      if (!isDoctor || disabled) {
        return;
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        return;
      }

      const tracks = collectAudioTracks(activeRoom);
      if (tracks.length === 0) {
        return;
      }

      const AudioContextCtor = window.AudioContext || (window as any).webkitAudioContext;
      const audioContext = new AudioContextCtor();
      if (audioContext.state === 'suspended' && audioContext.resume) {
        audioContext.resume().catch(() => undefined);
      }
      const destination = audioContext.createMediaStreamDestination();
      audioContextRef.current = audioContext;
      destinationRef.current = destination;
      sourcesRef.current = new Map();
      chunksRef.current = [];

      tracks.forEach(connectTrack);

      const mimeType = pickRecorderMimeType();
      const recorder = mimeType
        ? new MediaRecorder(destination.stream, { mimeType })
        : new MediaRecorder(destination.stream);
      mediaRecorderRef.current = recorder;

      recorder.addEventListener('dataavailable', event => {
        if (event.data && event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      });
      recorder.addEventListener('stop', () => {
        const mime = chunksRef.current[0] ? chunksRef.current[0].type : recorder.mimeType;
        const blob = chunksRef.current.length ? new Blob(chunksRef.current, { type: mime || 'audio/webm' }) : null;
        if (stopResolveRef.current) {
          stopResolveRef.current(blob && blob.size > 0 ? blob : null);
          stopResolveRef.current = null;
        }
      });

      recorder.start(1000);
    },
    [connectTrack, disabled, isDoctor]
  );

  const stashCurrentRecording = useCallback(async (): Promise<Blob[]> => {
    const recorder = mediaRecorderRef.current;
    const audioContext = audioContextRef.current;
    mediaRecorderRef.current = null;
    audioContextRef.current = null;
    destinationRef.current = null;
    sourcesRef.current.forEach(source => {
      try {
        source.disconnect();
      } catch {
        /* ignore */
      }
    });
    sourcesRef.current = new Map();

    let blob: Blob | null = null;
    if (recorder && recorder.state !== 'inactive') {
      blob = await new Promise(resolve => {
        stopResolveRef.current = resolve;
        try {
          if (recorder.state === 'recording') {
            recorder.requestData();
          }
        } catch {
          /* ignore */
        }
        recorder.stop();
        window.setTimeout(() => {
          if (stopResolveRef.current === resolve) {
            const mime = chunksRef.current[0] ? chunksRef.current[0].type : 'audio/webm';
            const fallback = chunksRef.current.length ? new Blob(chunksRef.current, { type: mime }) : null;
            stopResolveRef.current = null;
            resolve(fallback && fallback.size > 0 ? fallback : null);
          }
        }, 1500);
      });
    } else if (chunksRef.current.length) {
      blob = new Blob(chunksRef.current, { type: chunksRef.current[0].type || 'audio/webm' });
    }

    if (blob && blob.size > 0) {
      sessionBlobsRef.current = sessionBlobsRef.current.concat(blob);
    }
    chunksRef.current = [];

    if (audioContext && audioContext.state !== 'closed') {
      await audioContext.close().catch(() => undefined);
    }
    return sessionBlobsRef.current;
  }, []);

  useEffect(() => {
    if (disabled || !isDoctor || roomState !== 'connected' || !room) {
      return;
    }
    connectedOnceRef.current = true;
    if (room.name) {
      roomNameRef.current = room.name;
    }
    startRecording(room);
    if (startRetryRef.current) {
      window.clearInterval(startRetryRef.current);
    }
    let tries = 0;
    startRetryRef.current = window.setInterval(() => {
      tries += 1;
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        if (startRetryRef.current) {
          window.clearInterval(startRetryRef.current);
          startRetryRef.current = null;
        }
        return;
      }
      if (tries > 20) {
        if (startRetryRef.current) {
          window.clearInterval(startRetryRef.current);
          startRetryRef.current = null;
        }
        return;
      }
      startRecording(room);
    }, 1000);

    const handleTrackSubscribed = (track: { kind?: string; mediaStreamTrack?: MediaStreamTrack }) => {
      if (!track || track.kind !== 'audio' || !track.mediaStreamTrack) {
        return;
      }
      if (!mediaRecorderRef.current || mediaRecorderRef.current.state === 'inactive') {
        startRecording(room);
        return;
      }
      connectTrack(track.mediaStreamTrack);
    };
    const handleLocalPublished = (publication: { kind?: string; track?: LocalAudioTrack | null }) => {
      if (publication.kind === 'audio' && publication.track) {
        handleTrackSubscribed(publication.track);
      }
    };
    room.on('trackSubscribed', handleTrackSubscribed);
    room.localParticipant.on('trackPublished', handleLocalPublished);

    return () => {
      if (startRetryRef.current) {
        window.clearInterval(startRetryRef.current);
        startRetryRef.current = null;
      }
      room.off('trackSubscribed', handleTrackSubscribed);
      room.localParticipant.off('trackPublished', handleLocalPublished);
    };
  }, [connectTrack, disabled, isDoctor, room, roomState, startRecording]);

  useEffect(() => {
    if (disabled || !isDoctor || roomState !== 'disconnected' || !connectedOnceRef.current) {
      return;
    }

    const confirmedEnd = consumeSessionEndConfirmed();

    const run = async () => {
      if (confirmedEnd) {
        setOpen(true);
        setUploading(true);
        setError(null);
      }
      const segments = await stashCurrentRecording();
      if (!confirmedEnd) {
        return;
      }
      if (attemptedRef.current) {
        return;
      }
      attemptedRef.current = true;

      if (!segments.length) {
        setUploading(false);
        setError(new Error('No session audio was recorded. Rejoin the visit and end the session again.'));
        attemptedRef.current = false;
        return;
      }
      try {
        const wav = await mergeAudioBlobs(segments);
        await generateNotaReportFromAudio(wav, roomNameRef.current);
        sessionBlobsRef.current = [];
        setUploading(false);
        setOpen(false);
      } catch (err) {
        attemptedRef.current = false;
        setUploading(false);
        setError(err instanceof Error ? err : new Error('Failed to generate medical report'));
      }
    };

    void run();
  }, [disabled, isDoctor, roomState, stashCurrentRecording]);

  if (!open) {
    return null;
  }

  return (
    <Dialog open={open} maxWidth="sm" fullWidth disableBackdropClick={uploading} disableEscapeKeyDown={uploading}>
      <DialogTitle>{uploading ? 'Generating Medical Report' : 'Medical Report'}</DialogTitle>
      <DialogContent>
        {uploading && (
          <Box display="flex" flexDirection="column" alignItems="center" py={3}>
            <CircularProgress size={60} />
            <Typography variant="h6" style={{ marginTop: 16, textAlign: 'center' }}>
              Please wait while we generate the medical report and attach it to the appointment.
            </Typography>
            <Paper className={classes.disclaimer} elevation={0}>
              <Typography variant="body2" style={{ fontWeight: 'bold', marginBottom: 8 }}>
                Important:
              </Typography>
              <Typography variant="body2">
                The session audio is being analyzed by NOTA. This may take a minute. Please do not close this window.
              </Typography>
            </Paper>
          </Box>
        )}
        {error && (
          <Box className={classes.errorBox}>
            <Typography variant="h6" style={{ marginBottom: 8 }}>
              Report Error
            </Typography>
            <Typography variant="body2">{error.message}</Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {!uploading && error && (
          <Button onClick={() => setOpen(false)} color="primary" variant="contained">
            Dismiss
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
